'use client';

import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Text } from '@ironhack/design-system2/components/text';
import { type ReactElement, useRef } from 'react';

import { Button } from '@/app/components/client/button';
import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import { type PdpParamsParsed } from '@/app/l/[region]/[language]/pdp/[course]/[campus]/[[...extraParams]]/page';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import {
  type DatoBlock,
  type DatoButtonBlock,
  type DatoModuleFormat,
} from '@/lib/datocms/types';
import { type GaPageData, type WebCohort } from '@/types';
import { CohortSelectorCarousel } from './components/cohort-selector-carousel';

export type CohortSelectorData = DatoBlock<{
  cohortCardButtonText: string;
  emptyCohortsButton: DatoButtonBlock;
  fullTimeTabTitle?: string;
  highlightText?: string;
  moduleFormat: [DatoModuleFormat];
  partTimeTabTitle?: string;
  subtitle?: string;
  title?: string;
}>;

export type CohortSelectorProps = {
  data: CohortSelectorData;
  gaPageData: GaPageData;
  isPriceHidden: boolean | null;
  params: PdpParamsParsed;
} & BoxProps;

type Props = CohortSelectorProps & { cohorts: WebCohort[] };

export const CohortSelectorClient = (props: Props): ReactElement | null => {
  const {
    data: {
      cohortCardButtonText,
      emptyCohortsButton,
      fullTimeTabTitle,
      highlightText,
      moduleFormat: [moduleFormat],
      partTimeTabTitle,
      subtitle,
      title,
    },
    cohorts,
    gaPageData,
    isPriceHidden,
    params,
    ...boxProps
  } = props;

  const selectorRef = useRef<HTMLDivElement>(null);

  const { isEnabled } = useFeatureToggle();
  if (isEnabled('pdp-hero-form')) return null;

  const noCohorts = cohorts.length === 0;

  return (
    <ModuleWrapper
      innerProps={{ direction: 'column', gap: 3 }}
      moduleFormat={moduleFormat}
      // overflowX: 'clip' hides horizontal scroll from breaking container
      outerProps={{ overflowX: 'clip', ...boxProps }}
      ref={selectorRef}
    >
      {title && <Text textStyle={['3xl', null, '4xl']}>{title}</Text>}
      {highlightText && (
        <Text color="purple.100" textStyle="l">
          {highlightText}
        </Text>
      )}
      {subtitle && <Text textStyle="l">{subtitle}</Text>}
      {noCohorts ? (
        <Button {...emptyCohortsButton} />
      ) : (
        <CohortSelectorCarousel
          cohortCardButtonText={cohortCardButtonText}
          cohorts={cohorts}
          fullTimeTabTitle={fullTimeTabTitle}
          gaPageData={gaPageData}
          isPriceHidden={isPriceHidden}
          params={params}
          partTimeTabTitle={partTimeTabTitle}
        />
      )}
    </ModuleWrapper>
  );
};
